// src/pages/mushroom-customers-page.tsx

import {
  Button,
  CircularProgress,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
} from "react";
import LoadingBackdrop from "../components/loading-backdrop";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridDensity,
  GridInitialState,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridRowParams,
  GridRowClassNameParams,
  GridToolbar,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useHistory } from "react-router-dom";
import AppPage from "../components/page";
import ConfirmDialog from "../components/confirm-dialog";
import DeleteIcon from "@mui/icons-material/Delete";
import { ErrorContext, UserContext } from "../components/app-routes";
import { ActiveFilterControls } from "../components/active-filter-controls";
import mixpanel from "mixpanel-browser";
import { UpgradePlanDialog } from "../components/upgrade-plan-dialog";
import { getSearchParamNumber } from "../utils/history";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

// --------------------
// Local storage keys
// --------------------
const mushroomCustomersStorageKey = "mushroomCustomers";
const mushroomCustomersGridStateStorageKey = "mushroomCustomersGridState";
const mushroomCustomersGridDensityStorageKey = "mushroomCustomersGridDensity";

// --------------------
// Types
// --------------------
export type MushroomCustomer = {
  id: number;
  name: string;
  type: { name: string } | null;

  contactFirstName: string;
  contactLastName: string;
  contactEmail: string | null;

  addressLine1: string | null;
  addressLine2: string | null;
  locality: string | null;
  region: string | null;
  country: string | null;
  postalCode: string | null;

  businessEmail: string | null;
  businessPhoneNumber: string | null;

  contactPhoneNumber: string | null;

  deliveryInstructions: string | null;
  deliveryRoute: string | null;
  generalComments: string | null;
  otherPhoneNumber: string | null;
  paymentInstructions: string | null;
  website: string | null;

  archivedAt: string | null;
};

// --------------------
// Grid columns (same as SeedLeaf)
// --------------------
const columns: GridColDef[] = [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    hideable: false,
    headerName: "Checkbox",
  },
  {
    field: "archived",
    headerName: "",
    width: 35,
    minWidth: 35,
    sortable: false,
    filterable: false,
    align: "center",
    disableColumnMenu: true,
    disableExport: true,
    renderCell: (params) =>
      params.row.archivedAt ? (
        <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
      ) : null,
  },
  { field: "name", headerName: "Name", width: 200 },
  { field: "type", headerName: "Type", width: 200 },
  { field: "contactName", headerName: "Contact Name", width: 200 },
  {
    field: "contactEmail",
    headerName: "Email",
    width: 200,
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <a target="_blank" rel="noreferrer" href={`mailto:${params.value}`}>
          {params.value}
        </a>
      ) : (
        ""
      ),
  },
  { field: "addressLine1", headerName: "Address Line 1", width: 150 },
  { field: "addressLine2", headerName: "Address Line 2", width: 150 },
  { field: "locality", headerName: "City", width: 150 },
  { field: "region", headerName: "Province / State", width: 150 },
  { field: "country", headerName: "Country", width: 150 },
  { field: "postalCode", headerName: "Postal Code", width: 150 },
  {
    field: "businessEmail",
    headerName: "Business Email",
    width: 150,
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <a target="_blank" rel="noreferrer" href={`mailto:${params.value}`}>
          {params.value}
        </a>
      ) : (
        ""
      ),
  },
  { field: "businessPhone", headerName: "Business Phone", width: 150 },
  { field: "contactFirstName", headerName: "Contact First Name", width: 150 },
  { field: "contactLastName", headerName: "Contact Last Name", width: 150 },
  {
    field: "contactPhoneNumber",
    headerName: "Contact Phone Number",
    width: 150,
  },
  {
    field: "deliveryInstructions",
    headerName: "Delivery Instructions",
    width: 150,
  },
  { field: "deliveryRoute", headerName: "Delivery Route", width: 150 },
  { field: "generalComments", headerName: "General Comments", width: 150 },
  { field: "otherPhoneNumber", headerName: "Other Phone Number", width: 150 },
  {
    field: "paymentInstructions",
    headerName: "Payment Instructions",
    width: 150,
  },
  {
    field: "website",
    headerName: "Website",
    width: 150,
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <a target="_blank" rel="noreferrer" href={`${params.value}`}>
          {params.value}
        </a>
      ) : (
        ""
      ),
  },
];

const MushroomCustomersDataGrid = React.memo(
  (props: {
    customers: MushroomCustomer[] | null | undefined;
    onSelectionChange: (params: GridRowSelectionModel) => void;
    onPaginationModelChange: (m: GridPaginationModel) => void;
    initialPage?: number;
  }) => {
    const history = useHistory();
    const apiRef = useGridApiRef();
    const [initialState, setInitialState] = React.useState<GridInitialState>();

    const [paginationModel, setPaginationModel] =
      React.useState<GridPaginationModel>(() => {
        const raw = localStorage.getItem(mushroomCustomersGridStateStorageKey);

        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.pagination?.paginationModel;

            if (saved?.pageSize) {
              return {
                page: props.initialPage ?? saved.page ?? 0,
                pageSize: saved.pageSize,
              };
            }
          } catch {}
        }

        return {
          page: props.initialPage ?? 0,
          pageSize: 15,
        };
      });

    const saveSnapshot = React.useCallback(() => {
      if (apiRef?.current?.exportState) {
        const currentState = apiRef.current.exportState();

        if (!(currentState as any)?.pagination?.paginationModel?.pageSize) {
          (currentState as any).pagination = {
            paginationModel: { page: 0, pageSize: 15 },
          };
        }

        localStorage.setItem(
          mushroomCustomersGridStateStorageKey,
          JSON.stringify(currentState),
        );
      }
    }, [apiRef]);

    React.useLayoutEffect(() => {
      const raw = localStorage?.getItem(mushroomCustomersGridStateStorageKey);
      let parsed: GridInitialState | undefined;

      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch {
          localStorage.removeItem(mushroomCustomersGridStateStorageKey);
        }
      }

      setInitialState({
        ...parsed,
        columns: {
          columnVisibilityModel: {
            addressLine1: false,
            addressLine2: false,
            locality: false,
            region: false,
            country: false,
            postalCode: false,
            businessEmail: false,
            businessPhone: false,
            contactFirstName: false,
            contactLastName: false,
            contactPhoneNumber: false,
            deliveryInstructions: false,
            deliveryRoute: false,
            generalComments: false,
            otherPhoneNumber: false,
            paymentInstructions: false,
            website: false,
            ...(parsed?.columns?.columnVisibilityModel ?? {}),
          },
          orderedFields: [],
        },
        filter: undefined,
        sorting: parsed?.sorting ?? {
          sortModel: [{ field: "name", sort: "asc" }],
        },
        pagination: {
          paginationModel: {
            page:
              (parsed as any)?.pagination?.paginationModel?.page ??
              props.initialPage ??
              0,
            pageSize:
              (parsed as any)?.pagination?.paginationModel?.pageSize ?? 15,
          },
        },
      });

      window.addEventListener("beforeunload", saveSnapshot);

      return () => {
        window.removeEventListener("beforeunload", saveSnapshot);
        saveSnapshot();
      };
    }, [saveSnapshot, props.initialPage]);

    const [density, setDensity] = useState<GridDensity>(() => {
      const d = localStorage.getItem(
        mushroomCustomersGridDensityStorageKey,
      ) as GridDensity | null;
      return d === "compact" || d === "comfortable" || d === "standard"
        ? d
        : "standard";
    });

    const { grower } = useContext(UserContext);
    if (!grower) throw new Error("Invalid context");

    const csvFileName = React.useMemo(() => {
      const zone =
        (grower as any)?.TimeZone ||
        (grower as any)?.timeZone ||
        DateTime.local().zoneName;

      const now = DateTime.local().setZone(zone);
      return `Mushroom Customers List - ${now.toFormat("MMM dd, yyyy")}`;
    }, [grower]);

    if (!props.customers) return null;

    if (!initialState) return <CircularProgress />;

    return (
      <StripedDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={props.customers.map((c) => ({
          id: c.id,
          name: c.name,
          type: c.type?.name ?? "",
          contactName:
            `${c.contactFirstName ?? ""} ${c.contactLastName ?? ""}`.trim(),
          contactEmail: c.contactEmail,
          addressLine1: c.addressLine1,
          addressLine2: c.addressLine2,
          locality: c.locality,
          region: c.region,
          country: c.country,
          postalCode: c.postalCode,
          businessEmail: c.businessEmail,
          businessPhone: c.businessPhoneNumber,
          contactFirstName: c.contactFirstName,
          contactLastName: c.contactLastName,
          contactPhoneNumber: c.contactPhoneNumber,
          deliveryInstructions: c.deliveryInstructions,
          deliveryRoute: c.deliveryRoute,
          generalComments: c.generalComments,
          otherPhoneNumber: c.otherPhoneNumber,
          paymentInstructions: c.paymentInstructions,
          website: c.website,
          archivedAt: c.archivedAt,
        }))}
        initialState={{
          ...initialState,
          filter: undefined,
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => {
          setPaginationModel(m);
          saveSnapshot();
          props.onPaginationModelChange(m);
        }}
        showToolbar
        slots={{ toolbar: GridToolbar }}
        density={density}
        onDensityChange={(d) => {
          setDensity(d);
          localStorage.setItem(mushroomCustomersGridDensityStorageKey, d);
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) return "archived-row";
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams, event: React.MouseEvent) => {
          if (event.target instanceof HTMLAnchorElement) return;
          history.push(`/mushroom/home/customers/${params.id}`);
        }}
      />
    );
  },
);

export default function MushroomCustomersPage() {
  const [customers, setCustomers] = useState<MushroomCustomer[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [trashDialogOpen, setTrashDialogOpen] = useState(false);
  const { handleError } = useContext(ErrorContext);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addTypeName, setAddTypeName] = useState("Wholesale");
  const [addContactFirstName, setAddContactFirstName] = useState("");
  const [addContactLastName, setAddContactLastName] = useState("");
  const [addContactEmail, setAddContactEmail] = useState("");

  const showArchivedCheckedStorageKey = "mushroomCustomersShowArchivedChecked";
  const showActiveCheckedStorageKey = "mushroomCustomersShowActiveChecked";

  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );

  const { grower } = useContext(UserContext);
  const [openUpgradePlanDialog, setOpenUpgradePlanDialog] = useState(false);
  const history = useHistory();
  const isMounted = useRef(true);

  if (!grower) throw new Error("Invalid context");

  const onClickCancelUpgradePlan = () => setOpenUpgradePlanDialog(false);

  const readCustomers = () => {
    try {
      const raw = window.localStorage.getItem(mushroomCustomersStorageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as MushroomCustomer[]) : [];
    } catch (e) {
      handleError(e);
      return [];
    }
  };

  const writeCustomers = (next: MushroomCustomer[]) => {
    window.localStorage.setItem(
      mushroomCustomersStorageKey,
      JSON.stringify(next),
    );
  };

  const handleTrashDialogClose = async (okTrash?: boolean) => {
    setTrashDialogOpen(false);
    if (!okTrash) return;

    setBusy(true);

    try {
      const current = readCustomers();
      const next = current.filter((c) => !selected.includes(c.id));
      writeCustomers(next);

      // analytics parity
      if (selected.length) mixpanel.track("Delete Customer");

      if (isMounted.current) {
        setCustomers(next);
        setSelected([]);
      }
    } catch (e) {
      handleError(e);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    setBusy(true);

    try {
      let list = readCustomers();

      if (!list.length) {
        list = [
          {
            id: 1,
            name: "Coastal Market",
            type: { name: "Wholesale" },

            contactFirstName: "Jamie",
            contactLastName: "L",
            contactEmail: "orders@coastal.example",

            addressLine1: "123 Harbor St",
            addressLine2: null,
            locality: "Vancouver",
            region: "BC",
            country: "CA",
            postalCode: "V5K 0A1",

            businessEmail: "orders@coastal.example",
            businessPhoneNumber: "(555) 010-1001",

            contactPhoneNumber: "(555) 010-2001",

            deliveryInstructions: "Deliver Tue mornings",
            deliveryRoute: "Route A",
            generalComments: null,
            otherPhoneNumber: null,
            paymentInstructions: "Net 14",
            website: "https://coastal.example",

            archivedAt: null,
          },
          {
            id: 2,
            name: "Forest Café",
            type: { name: "Restaurant" },

            contactFirstName: "Morgan",
            contactLastName: "K",
            contactEmail: "morgan@forest.example",

            addressLine1: "88 Cedar Ave",
            addressLine2: null,
            locality: "Burnaby",
            region: "BC",
            country: "CA",
            postalCode: "V5C 1A1",

            businessEmail: null,
            businessPhoneNumber: "(555) 010-1002",

            contactPhoneNumber: null,

            deliveryInstructions: "Call on arrival",
            deliveryRoute: "Route B",
            generalComments: "Prefers 150g packs",
            otherPhoneNumber: null,
            paymentInstructions: null,
            website: null,

            archivedAt: null,
          },
          {
            id: 3,
            name: "Sunrise Foods",
            type: { name: "Grocery" },

            contactFirstName: "Avery",
            contactLastName: "P",
            contactEmail: "purchasing@sunrise.example",

            addressLine1: null,
            addressLine2: null,
            locality: null,
            region: null,
            country: null,
            postalCode: null,

            businessEmail: "purchasing@sunrise.example",
            businessPhoneNumber: null,

            contactPhoneNumber: null,

            deliveryInstructions: null,
            deliveryRoute: null,
            generalComments: "Wholesale – needs COA on request",
            otherPhoneNumber: null,
            paymentInstructions: null,
            website: null,

            archivedAt: DateTime.local()
              .minus({ days: 7 })
              .toFormat("yyyy-LL-dd"),
          },
        ];

        writeCustomers(list);
      }

      if (isMounted.current) setCustomers(list);
    } catch (e) {
      if (isMounted.current) handleError(e);
    } finally {
      if (isMounted.current) setBusy(false);
    }

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectionChange = useCallback((m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;

    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }

    // keep SeedLeaf behavior: only allow 1 selected
    setSelected(ids.length ? [ids[0]] : []);
  }, []);

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleOnClickAdd = () => {
    if (grower.Plan === "hobby" && customers && customers.length >= 5) {
      setOpenUpgradePlanDialog(true);
      return;
    }

    setAddName("");
    setAddTypeName("Wholesale");
    setAddContactFirstName("");
    setAddContactLastName("");
    setAddContactEmail("");
    setAddOpen(true);
  };

  const handleCloseAdd = () => {
    setAddOpen(false);
  };

  const handleSaveAdd = () => {
    if (!addName.trim()) {
      return;
    }

    const current = readCustomers();
    const maxId = current.reduce((m, c) => Math.max(m, c.id), 0);

    const next: MushroomCustomer = {
      id: maxId + 1,
      name: addName.trim(),
      type: addTypeName.trim() ? { name: addTypeName.trim() } : null,

      contactFirstName: addContactFirstName.trim(),
      contactLastName: addContactLastName.trim(),
      contactEmail: addContactEmail.trim() ? addContactEmail.trim() : null,

      addressLine1: null,
      addressLine2: null,
      locality: null,
      region: null,
      country: null,
      postalCode: null,

      businessEmail: null,
      businessPhoneNumber: null,

      contactPhoneNumber: null,

      deliveryInstructions: null,
      deliveryRoute: null,
      generalComments: null,
      otherPhoneNumber: null,
      paymentInstructions: null,
      website: null,

      archivedAt: null,
    };

    const updated = [next, ...current];
    writeCustomers(updated);
    setCustomers(updated);
    setAddOpen(false);
  };

  const page = getSearchParamNumber(history, "page", 0);

  return (
    <AppPage title={"Customers"}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button variant="contained" color="primary" onClick={handleOnClickAdd}>
          Add
        </Button>

        <IconButton
          aria-label="trash"
          disabled={busy || selected.length === 0}
          onClick={() => setTrashDialogOpen(true)}
          size="large"
        >
          <DeleteIcon />
        </IconButton>

        <ActiveFilterControls
          showActiveChecked={showActiveChecked}
          showArchivedChecked={showArchivedChecked}
          onChangeShowActive={handleShowActiveChanged}
          onChangeShowArchived={handleShowArchivedChanged}
        />
      </Box>

      <Box sx={{ height: 750, width: "100%", pt: 2 }}>
        <MushroomCustomersDataGrid
          customers={customers?.filter(
            (c) =>
              (showActiveChecked && c.archivedAt === null) ||
              (showArchivedChecked && c.archivedAt !== null),
          )}
          onSelectionChange={handleSelectionChange}
          initialPage={page}
          onPaginationModelChange={(m) => {
            const params = new URLSearchParams(`page=${m.page}`);
            history.replace({ search: params.toString() });
          }}
        />
      </Box>

      <LoadingBackdrop open={busy} />

      <ConfirmDialog
        title={`Trash Customer${selected.length > 1 ? "s" : ""}`}
        description={`Are you sure you want to trash ${selected.length} customer${
          selected.length > 1 ? "s" : ""
        }?`}
        open={trashDialogOpen}
        onClose={handleTrashDialogClose}
      />

      <UpgradePlanDialog
        open={openUpgradePlanDialog}
        onClickCancel={onClickCancelUpgradePlan}
        text="Sorry, the Hobby Plan is limited to 5 customers."
      />

      <Dialog open={addOpen} onClose={handleCloseAdd} fullWidth maxWidth="sm">
        <DialogTitle>Add Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              fullWidth
            />

            <TextField
              label="Type"
              value={addTypeName}
              onChange={(e) => setAddTypeName(e.target.value)}
              select
              fullWidth
            >
              <MenuItem value="Wholesale">Wholesale</MenuItem>
              <MenuItem value="Restaurant">Restaurant</MenuItem>
              <MenuItem value="Grocery">Grocery</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>

            <TextField
              label="Contact First Name"
              value={addContactFirstName}
              onChange={(e) => setAddContactFirstName(e.target.value)}
              fullWidth
            />

            <TextField
              label="Contact Last Name"
              value={addContactLastName}
              onChange={(e) => setAddContactLastName(e.target.value)}
              fullWidth
            />

            <TextField
              label="Contact Email"
              value={addContactEmail}
              onChange={(e) => setAddContactEmail(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdd} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAdd}
            color="primary"
            variant="contained"
            disabled={busy || !addName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </AppPage>
  );
}
